import { Injectable, inject, signal, effect, computed, DestroyRef } from '@angular/core';
import * as THREE from 'three';

import { ViewerCoreService } from './viewer-core.service.js';
import { SceneStateService } from './scene-state.service';
import { PointShape, PointSizeType } from '../shared/defines.js';

@Injectable({ providedIn: 'root' })
export class MaterialService {
  private core = inject(ViewerCoreService);
  private sceneState = inject(SceneStateService);
  private destroyRef = inject(DestroyRef);

  // ─── Reactive state ─────────────────────────────────────────────────────────

  readonly colorAttribute = signal<string>('rgba');
  readonly pointSize = signal(0.6);
  readonly pointShape = signal<string>('SQUARE');
  readonly pointSizeType = signal<string>('ADAPTIVE');
  readonly solidColor = signal('#ffffff');

  // RGB controls
  readonly rgbGamma = signal(1.0);
  readonly rgbBrightness = signal(0.0);
  readonly rgbContrast = signal(0.0);

  // Intensity controls
  readonly intensityGamma = signal(1.0);
  readonly intensityBrightness = signal(0.0);
  readonly intensityContrast = signal(0.0);

  private _syncing = false;

  constructor() {
    effect(() => {
      const viewer = this.core.viewer();
      if (!viewer) return;
      this._syncFromViewer(viewer);
    });

    // Push effects
    effect(() => {
      const attr = this.colorAttribute();
      this._withMaterial(m => (m.activeAttributeName = attr));
    });

    effect(() => {
      const size = this.pointSize();
      this._withMaterial(m => (m.size = size));
    });

    effect(() => {
      const shape = this.pointShape();
      const shapeEnum = (PointShape as any)[shape];
      if (shapeEnum !== undefined) this._withMaterial(m => (m.shape = shapeEnum));
    });

    effect(() => {
      const sizeType = this.pointSizeType();
      const sizeTypeEnum = (PointSizeType as any)[sizeType];
      if (sizeTypeEnum !== undefined) this._withMaterial(m => (m.pointSizeType = sizeTypeEnum));
    });

    effect(() => {
      const hex = this.solidColor();
      if (this._syncing) return;
      const pointclouds = this.sceneState.pointclouds();
      if (!pointclouds.length) return;
      try {
        const color = new THREE.Color(hex);
        for (const pc of pointclouds) {
          if (pc?.material) pc.material.color = color;
        }
      } catch {
        console.warn(`Invalid color hex: ${hex}`);
      }
    });

    effect(() => {
      const v = this.rgbGamma();
      this._withMaterial(m => (m.rgbGamma = v));
    });

    effect(() => {
      const v = this.rgbBrightness();
      this._withMaterial(m => (m.rgbBrightness = v));
    });

    effect(() => {
      const v = this.rgbContrast();
      this._withMaterial(m => (m.rgbContrast = v));
    });

    effect(() => {
      const v = this.intensityGamma();
      this._withMaterial(m => (m.intensityGamma = v));
    });

    effect(() => {
      const v = this.intensityBrightness();
      this._withMaterial(m => (m.intensityBrightness = v));
    });

    effect(() => {
      const v = this.intensityContrast();
      this._withMaterial(m => (m.intensityContrast = v));
    });
  }

  // ─── Computed / derived ─────────────────────────────────────────────────────

  getColorAttributes(): { value: string; label: string }[] {
    const pointclouds = this.sceneState.pointclouds();
    if (!pointclouds.length) {
      return [
        { value: 'rgba', label: 'RGB' },
        { value: 'elevation', label: 'Elevation' },
        { value: 'intensity', label: 'Intensity' },
        { value: 'intensity gradient', label: 'Intensity Gradient' },
        { value: 'classification', label: 'Classification' },
        { value: 'color', label: 'Solid Color' },
      ];
    }

    const pointcloud = pointclouds[0];
    if (!pointcloud?.pcoGeometry) {
      return [];
    }

    const attributes = pointcloud.pcoGeometry.pointAttributes.attributes;
    const options: { value: string; label: string }[] = [];

    options.push(
      ...attributes.map((a: any) => ({
        value: a.name,
        label: this.formatAttributeName(a.name),
      })),
    );

    const intensityIndex = options.findIndex(o => o.value === 'intensity');
    if (intensityIndex >= 0) {
      options.splice(intensityIndex + 1, 0, {
        value: 'intensity gradient',
        label: 'Intensity Gradient',
      });
    }

    options.push(
      { value: 'elevation', label: 'Elevation' },
      { value: 'color', label: 'Solid Color' },
    );

    const blacklist = [
      'POSITION_CARTESIAN',
      'position',
      'point source id',
      'return number',
      'number of returns',
      'source id',
      'gps-time',
      'scan angle rank',
      'user data',
    ];

    return options.filter(o => !blacklist.includes(o.value));
  }

  // ─── Static options ─────────────────────────────────────────────────────────

  getPointShapeOptions(): { value: string; label: string }[] {
    return [
      { value: 'SQUARE', label: 'Square' },
      { value: 'CIRCLE', label: 'Circle' },
      { value: 'PARABOLOID', label: 'Paraboloid' },
    ];
  }

  getPointSizeTypeOptions(): { value: string; label: string }[] {
    return [
      { value: 'FIXED', label: 'Fixed' },
      { value: 'ATTENUATED', label: 'Attenuated' },
      { value: 'ADAPTIVE', label: 'Adaptive' },
    ];
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private _syncFromViewer(viewer: any): void {
    this._syncing = true;
    const pc = this.sceneState.pointclouds()[0];
    if (pc?.material) {
      const mat = pc.material;
      this.colorAttribute.set(mat.activeAttributeName || 'rgba');
      this.pointSize.set(mat.size ?? 0.6);

      for (const [key, value] of Object.entries(PointShape)) {
        if (value === mat.shape) {
          this.pointShape.set(key);
          break;
        }
      }
      for (const [key, value] of Object.entries(PointSizeType)) {
        if (value === mat.pointSizeType) {
          this.pointSizeType.set(key);
          break;
        }
      }

      if (mat.color) this.solidColor.set('#' + mat.color.getHexString());
      this.rgbGamma.set(mat.rgbGamma ?? 1.0);
      this.rgbBrightness.set(mat.rgbBrightness ?? 0.0);
      this.rgbContrast.set(mat.rgbContrast ?? 0.0);
      this.intensityGamma.set(mat.intensityGamma ?? 1.0);
      this.intensityBrightness.set(mat.intensityBrightness ?? 0.0);
      this.intensityContrast.set(mat.intensityContrast ?? 0.0);
    }
    this._syncing = false;
  }

  private _withMaterial(fn: (material: any) => void): void {
    if (this._syncing) return;
    const pc = this.sceneState.pointclouds()[0];
    if (pc?.material) fn(pc.material);
  }

  private formatAttributeName(name: string): string {
    const nameMap: Record<string, string> = {
      rgba: 'RGB',
      intensity: 'Intensity',
      classification: 'Classification',
      elevation: 'Elevation',
      'gps-time': 'GPS Time',
      'return number': 'Return Number',
      'number of returns': 'Number of Returns',
      'source id': 'Source ID',
    };

    return (
      nameMap[name] ||
      name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    );
  }
}
